<template>
  <div class="tt-view">
    <TechnicianTopBar title="Mis tickets" :subtitle="parkName" />

    <div v-if="loading" class="tt-state"><span class="mdi mdi-loading tt-state__spinner"></span></div>

    <div v-else-if="blocked" class="tt-state tt-state--message">
      <span class="mdi mdi-lock-outline tt-state__icon"></span>
      <p>{{ blocked }}</p>
    </div>

    <div v-else-if="loadError" class="tt-state tt-state--message">
      <span class="mdi mdi-alert-circle-outline tt-state__icon"></span>
      <p>{{ loadError }}</p>
      <button class="tt-btn tt-btn--primary tt-btn--inline" @click="load">Reintentar</button>
    </div>

    <template v-else>
      <div class="tt-filters">
        <div class="tt-tabs">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="tt-tab"
            :class="{ 'tt-tab--active': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
            <span class="tt-tab__badge">{{ counts[tab.key] }}</span>
          </button>
        </div>
        <div class="tt-search">
          <span class="mdi mdi-magnify"></span>
          <input v-model="search" type="text" placeholder="Buscar ticket..." />
        </div>
      </div>

      <div class="tt-list">
        <div v-if="!visibleItems.length" class="tt-empty">{{ emptyText }}</div>

        <article
          v-for="t in visibleItems"
          :key="(t.kind || 'TICKET') + t.id"
          class="tt-card"
          :style="{ borderLeftColor: t.kind === 'SCHEDULED' ? t.color : priorityColor(t.priority) }"
          @click="openItem(t)"
        >
          <div class="tt-card__top">
            <span class="tt-card__code">
              {{ t.code }}
            </span>
            <span v-if="t.kind === 'SCHEDULED'" class="tt-card__priority" :style="{ backgroundColor: t.color }">Preventivo programado</span>
            <span v-else class="tt-card__priority" :style="{ backgroundColor: priorityColor(t.priority) }">
              {{ PRIORITY_INFO[t.priority]?.label }}
            </span>
          </div>

          <div class="tt-card__title">{{ t.title }}</div>

          <div class="tt-card__place">
            <span v-if="t.tenantName">{{ t.tenantName }}</span>
            <span class="tt-card__tag">{{ t.plotName }}</span>
          </div>

          <div class="tt-card__time">
            <span v-if="t.kind === 'SCHEDULED'">Programado: {{ formatShortDateTime(t.startAt) }}</span>
            <span v-else>{{ formatShortDateTime(t.createdAt) }}</span>
            <span v-if="t.sla && t.sla.state !== 'none'" class="tt-card__sla" :style="{ color: SLA_COLORS[t.sla.state] }">
              {{ slaText(t.sla) }}
            </span>
          </div>

          <div v-if="t.status === 'WAITING'" class="tt-card__wait">
            En espera: {{ WAIT_REASONS[t.waitReason] || 'en pausa' }}
          </div>

          <div class="tt-card__actions" @click.stop>
            <template v-if="t.kind === 'SCHEDULED'">
              <button class="tt-btn tt-btn--outline tt-btn--grow" @click="openItem(t)">Ver detalle</button>
            </template>
            <template v-else-if="t.tab === 'PENDING'">
              <button class="tt-btn tt-btn--primary tt-btn--grow" :disabled="busyId === t.id" @click="onConfirm(t)">
                Confirmar asignación
              </button>
              <button class="tt-btn tt-btn--outline tt-btn--desktop-only" @click="openDetail(t.id)">Ver detalle</button>
            </template>
            <template v-else-if="t.tab === 'ACTIVE' && t.status !== 'WAITING'">
              <button class="tt-btn tt-btn--primary tt-btn--half" @click="openDetail(t.id, true)">Actualizar estatus</button>
              <button class="tt-btn tt-btn--outline tt-btn--half" @click="openDetail(t.id)">Ver detalle</button>
            </template>
            <template v-else>
              <button class="tt-btn tt-btn--outline tt-btn--grow" @click="openDetail(t.id)">Ver detalle</button>
            </template>
          </div>
        </article>
      </div>
    </template>

    <div v-if="toast" class="tt-toast" :class="{ 'tt-toast--error': toastError }">{{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import TechnicianTopBar from '@/components/Technician/TechnicianTopBar.vue'
import {
  getMyTickets, updateTicketStage,
  PRIORITY_INFO, WAIT_REASONS, SLA_COLORS, slaText, formatShortDateTime,
} from '@/services/technicianPortal'

const router = useRouter()

const TABS = [
  { key: 'ACTIVE', label: 'Activos' },
  { key: 'PENDING', label: 'Pendientes' },
  { key: 'RESOLVED', label: 'Resueltos' },
]

const loading = ref(true)
const loadError = ref('')
const blocked = ref('')
const parkName = ref('')
const items = ref([])
const counts = ref({ ACTIVE: 0, PENDING: 0, RESOLVED: 0 })
const activeTab = ref('ACTIVE')
const search = ref('')
const busyId = ref(null)

const BLOCKED_MESSAGES = {
  PORTAL_DISABLED: 'El portal de técnicos no está habilitado en este parque. Pide al administrador que lo active en Configuración.',
  TECHNICIAN_PROFILE_REQUIRED: 'Tu usuario no está vinculado a un técnico del catálogo. Pide al administrador que lo vincule.',
  TECHNICIAN_INACTIVE: 'Tu perfil de técnico está inactivo. Habla con el administrador del parque.',
}

const priorityColor = (p) => PRIORITY_INFO[p]?.color || '#94a3b8'

async function load() {
  loading.value = true
  loadError.value = ''
  blocked.value = ''
  try {
    const data = await getMyTickets()
    parkName.value = data.parkName
    items.value = data.items
    counts.value = data.counts
  } catch (err) {
    if (BLOCKED_MESSAGES[err.message]) blocked.value = BLOCKED_MESSAGES[err.message]
    else loadError.value = 'No se pudieron cargar tus tickets.'
  } finally {
    loading.value = false
  }
}

// El servidor ya los manda en orden de urgencia; aquí solo se filtra por pestaña y búsqueda.
const visibleItems = computed(() => {
  const q = search.value.trim().toLowerCase()
  return items.value
    .filter((t) => t.tab === activeTab.value)
    .filter(
      (t) =>
        !q ||
        [t.code, t.title, t.description, t.plotName, t.tenantName].some((v) => (v || '').toLowerCase().includes(q))
    )
})

const emptyText = computed(() => {
  if (search.value.trim()) return 'Ningún ticket coincide con tu búsqueda.'
  return { ACTIVE: 'No tienes tickets activos.', PENDING: 'No tienes tickets por confirmar.', RESOLVED: 'Aún no hay tickets resueltos recientes.' }[activeTab.value]
})

function openItem(t) {
  if (t.kind === 'SCHEDULED') router.push(`/tecnico/calendario/${t.id}`)
  else openDetail(t.id)
}

function openDetail(id, focusStatus = false) {
  router.push({ path: `/tecnico/ticket/${id}`, query: focusStatus ? { focus: 'status' } : {} })
}

const toast = ref('')
const toastError = ref(false)
let toastTimer = null
function showToast(message, isError = false) {
  toast.value = message
  toastError.value = isError
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => { toast.value = '' }, 3000)
}

const CONFIRM_ERRORS = {
  TICKET_ALREADY_RESOLVED: 'Este ticket ya fue resuelto.',
  TICKET_ON_HOLD: 'Este ticket está en espera; lo reactiva el administrador.',
}

async function onConfirm(ticket) {
  busyId.value = ticket.id
  try {
    await updateTicketStage(ticket.id, 'CONFIRMED')
    await load()
    activeTab.value = 'ACTIVE'
    showToast(`${ticket.code} confirmado`)
  } catch (err) {
    showToast(CONFIRM_ERRORS[err.message] || 'No se pudo confirmar el ticket.', true)
  } finally {
    busyId.value = null
  }
}

onMounted(load)
</script>

<style scoped>
.tt-view { display: flex; flex-direction: column; flex: 1; min-height: 0; background: #f8f9fc; font-size: 14px; }

.tt-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: #64748b; padding: 24px; text-align: center; }
.tt-state__spinner { font-size: 1.8rem; animation: tt-spin 0.8s linear infinite; }
.tt-state__icon { font-size: 2rem; color: #94a3b8; }
.tt-state p { margin: 0; max-width: 360px; font: 400 14px var(--font-body); }
@keyframes tt-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.tt-filters {
  display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;
  min-height: 48px; padding: 6px 16px; background: #fff; border-bottom: 1px solid #f1f5f9; flex-shrink: 0;
}
.tt-tabs { display: flex; gap: 6px; }
.tt-tab {
  display: flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: 16px;
  border: 1px solid #e2e8f0; background: #fff; cursor: pointer; font: 500 12px var(--font-body); color: #64748b;
}
.tt-tab--active { background: #1e3a5f; border-color: #1e3a5f; color: #fff; }
.tt-tab__badge {
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: #f1f5f9; color: #64748b;
  font: 600 10px var(--font-body); display: inline-flex; align-items: center; justify-content: center;
}
.tt-tab--active .tt-tab__badge { background: rgba(255, 255, 255, 0.2); color: #fff; }

.tt-search { display: flex; align-items: center; gap: 4px; width: 160px; padding: 0 8px; height: 32px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #94a3b8; }
.tt-search input { flex: 1; min-width: 0; border: none; outline: none; font: 400 12px var(--font-body); color: #334155; background: transparent; }

.tt-list { flex: 1; overflow-y: auto; padding: 12px 16px 24px; display: flex; flex-direction: column; gap: 12px; align-items: stretch; }
.tt-list::-webkit-scrollbar { width: 6px; }
.tt-list::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 3px; }
.tt-list::-webkit-scrollbar-track { background: transparent; }
.tt-empty { text-align: center; color: #94a3b8; font: 400 13px var(--font-body); padding: 40px 0; }

.tt-card {
  background: #fff; border: 1px solid #e2e8f0; border-left: 3px solid #94a3b8; border-radius: 8px;
  padding: 12px 14px; display: flex; flex-direction: column; gap: 6px; cursor: pointer;
  width: 100%; max-width: 600px; margin: 0 auto;
}
.tt-card__top { display: flex; align-items: center; justify-content: space-between; }
.tt-card__code { font: 400 12px var(--font-mono); color: #64748b; display: flex; align-items: center; gap: 6px; }
.tt-card__wa { font-size: 12px; color: #25d366; }
.tt-card__priority { font: 500 10px var(--font-body); color: #fff; padding: 2px 8px; border-radius: 10px; }
.tt-card__title {
  font: 500 14px var(--font-body); color: #1e3a5f; line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.tt-card__place { display: flex; align-items: center; gap: 8px; font: 400 12px var(--font-body); color: #64748b; flex-wrap: wrap; }
.tt-card__tag { background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 1px 7px; font: 500 11px var(--font-body); }
.tt-card__time { display: flex; align-items: center; justify-content: space-between; gap: 8px; font: 400 11px var(--font-body); color: #94a3b8; }
.tt-card__sla { font-weight: 500; text-align: right; }
.tt-card__wait { font: 500 11px var(--font-body); color: #b45309; background: #fef3c7; border-radius: 6px; padding: 4px 8px; }

.tt-card__actions { display: flex; gap: 8px; margin-top: 6px; }
.tt-btn {
  height: 36px; padding: 0 14px; border-radius: 6px; cursor: pointer; font: 500 13px var(--font-body);
  display: inline-flex; align-items: center; justify-content: center; white-space: nowrap;
}
.tt-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.tt-btn--primary { background: #3b82f6; border: 1px solid #3b82f6; color: #fff; }
.tt-btn--outline { background: #fff; border: 1px solid #e2e8f0; color: #334155; }
.tt-btn--inline { margin-top: 4px; }
.tt-btn--half { flex: 1; }
.tt-btn--desktop-only { flex: 1; }
.tt-btn--grow { flex: 0 0 60%; }
.tt-card__actions > .tt-btn--grow:only-child { flex: 1; }

.tt-toast {
  position: fixed; left: 50%; transform: translateX(-50%); bottom: 76px; z-index: 3000;
  background: #10b981; color: #fff; padding: 10px 16px; border-radius: 6px; font: 500 13px var(--font-body);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18); max-width: calc(100vw - 32px);
}
.tt-toast--error { background: #dc2626; }

@media (max-width: 768px) {
  .tt-btn { height: 44px; }
  .tt-btn--grow { flex: 1; }
  .tt-btn--desktop-only { display: none; }
  .tt-card { max-width: none; }
  .tt-search { width: 100%; }
  .tt-tabs { width: 100%; }
  .tt-tab { flex: 1; justify-content: center; }
}
</style>